import type { IntakeAnswerValue, IntakeQuestion } from '@/api/portalIntake';

export interface IntakeSection {
  section: string;
  questions: IntakeQuestion[];
}

/** Group questions into sections, preserving registry order — first
 *  appearance of a section name fixes its position. */
export function groupBySection(questions: IntakeQuestion[]): IntakeSection[] {
  const sections: IntakeSection[] = [];
  const byName = new Map<string, IntakeSection>();
  for (const q of questions) {
    let section = byName.get(q.section);
    if (!section) {
      section = { section: q.section, questions: [] };
      byName.set(q.section, section);
      sections.push(section);
    }
    section.questions.push(q);
  }
  return sections;
}

/** Human-readable rendering of one answer — option labels for selects,
 *  Yes/No for booleans, an em dash for unanswered. */
export function answerLabel(
  question: IntakeQuestion,
  value: IntakeAnswerValue | undefined,
): string {
  if (value === null || value === undefined || value === '') return '—';
  if (question.answer_type === 'boolean') return value === true ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    return value.map((v) => optionLabel(question, v)).join(', ');
  }
  if (question.answer_type === 'single_select') return optionLabel(question, String(value));
  return String(value);
}

function optionLabel(question: IntakeQuestion, value: string): string {
  return question.options.find((o) => o.value === value)?.label ?? value;
}

/** True when the answer counts toward the required-question gate. */
export function isAnswered(value: IntakeAnswerValue | undefined): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}
